/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from "react";
import GenericCard from "@ui-components/Card/GenericCard";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericInput from "@ui-components/Input/GenericInput";
import { GenericTable, GenericTableColumn } from "@ui-components/Table/GenericTable";
import { ReactComponent as IconEdit } from "../../../../../../shared/assets/Icons/Icon_Edit.svg";
import { ReactComponent as IconDelete } from "../../../../../../shared/assets/Icons/Icon_Delete.svg";
import { ReactComponent as IconCheck } from "../../../../../../shared/assets/Icons/IconCheckSquared.svg";
import { ReactComponent as IconCancel } from "../../../../../../shared/assets/Icons/IconCrossSquare.svg";
import { ReactComponent as IconPlus } from "../../../../../../shared/assets/Icons/IconPlusNV.svg";

export interface P2PAddressingRow {
  id: string;
  network: string;
  gateway: string;
  peerIp: string;
  mask: string | number | null;
}

export interface P2PAddressingTableProps {
  p2pAddressing: {
    mask: string | number | null;
    peerIp: string;
    gateway: string;
    network: string;
  };
  onUpdate: (p2pAddressing: {
    mask: string | number | null;
    peerIp: string;
    gateway: string;
    network: string;
  }) => void;
}

export const P2PAddressingTable: React.FC<P2PAddressingTableProps> = ({
  p2pAddressing,
  onUpdate,
}) => {
  const [p2pRows, setP2pRows] = useState<P2PAddressingRow[]>([]);
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [editingRowData, setEditingRowData] = useState<P2PAddressingRow | null>(null);

  // Initialize P2P rows from form data
  useEffect(() => {
    if (p2pAddressing.network || p2pAddressing.gateway || 
        p2pAddressing.peerIp || p2pAddressing.mask !== null) {
      const maskValue = p2pAddressing.mask;
      const maskDisplay = maskValue !== null && maskValue !== undefined 
        ? (typeof maskValue === 'string' ? maskValue : String(maskValue))
        : null;
      
      setP2pRows([{
        id: `p2p-${Date.now()}`,
        network: p2pAddressing.network || "",
        gateway: p2pAddressing.gateway || "",
        peerIp: p2pAddressing.peerIp || "",
        mask: maskDisplay,
      }]);
    } else {
      setP2pRows([]);
    }
  }, [p2pAddressing]);

  const handleAddP2PRow = () => {
    const newRow: P2PAddressingRow = {
      id: `p2p-${Date.now()}-${Math.random()}`,
      network: "",
      gateway: "",
      peerIp: "",
      mask: null,
    };
    setP2pRows([...p2pRows, newRow]);
    setEditingRowId(newRow.id);
    setEditingRowData({ ...newRow });
  };

  const handleEditP2PRow = (rowId: string) => {
    const row = p2pRows.find(r => r.id === rowId);
    if (row) {
      setEditingRowId(rowId);
      setEditingRowData({ ...row });
    }
  };

  const handleSaveP2PRow = (rowId: string) => {
    if (editingRowData) {
      const updatedRows = p2pRows.map(row => 
        row.id === rowId ? editingRowData : row
      );
      setP2pRows(updatedRows);
      setEditingRowId(null);
      setEditingRowData(null);
      
      // Update form data with the first row (API might only support one P2P entry)
      if (updatedRows.length > 0) {
        const firstRow = updatedRows[0];
        // Handle mask - keep as string if it's an IP mask format, otherwise convert to number
        const maskValue = firstRow.mask;
        let finalMask: string | number | null = null;
        if (maskValue !== null && maskValue !== undefined) {
          const maskStr = String(maskValue);
          const isIpMask = maskStr.includes('.');
          finalMask = isIpMask ? maskStr : (isNaN(Number(maskStr)) ? maskStr : Number(maskStr));
        }
        
        onUpdate({
          network: firstRow.network,
          gateway: firstRow.gateway,
          peerIp: firstRow.peerIp,
          mask: finalMask,
        });
      }
    }
  };

  const handleCancelP2PRow = () => {
    if (editingRowId && editingRowData) {
      const isEmpty = !editingRowData.network && !editingRowData.gateway && 
                      !editingRowData.peerIp && editingRowData.mask === null;
      
      if (isEmpty) {
        setP2pRows(p2pRows.filter(row => row.id !== editingRowId));
      }
    }
    setEditingRowId(null);
    setEditingRowData(null);
  };

  const handleDeleteP2PRow = (rowId: string) => {
    const newRows = p2pRows.filter(row => row.id !== rowId);
    setP2pRows(newRows);
    
    if (newRows.length === 0) {
      onUpdate({
        network: "",
        gateway: "",
        peerIp: "",
        mask: null,
      });
    } else {
      const firstRow = newRows[0];
      const maskValue = firstRow.mask;
      let finalMask: string | number | null = null;
      if (maskValue !== null && maskValue !== undefined) {
        const maskStr = String(maskValue);
        const isIpMask = maskStr.includes('.');
        finalMask = isIpMask ? maskStr : (isNaN(Number(maskStr)) ? maskStr : Number(maskStr));
      }
      
      onUpdate({
        network: firstRow.network,
        gateway: firstRow.gateway,
        peerIp: firstRow.peerIp,
        mask: finalMask,
      });
    }
  };

  const handleEditingRowChange = (field: keyof P2PAddressingRow, value: string | number | null) => {
    if (editingRowData) {
      setEditingRowData({
        ...editingRowData,
        [field]: value,
      });
    }
  };

  // Define columns for GenericTable
  const p2pTableColumns: GenericTableColumn<P2PAddressingRow>[] = [
    {
      header: "Network",
      accessor: "network",
      renderCell: (row) => {
        if (editingRowId === row.id) {
          return (
            <GenericInput
              label=""
              value={editingRowData?.network || ""}
              onChange={(value) => handleEditingRowChange("network", value)}
              placeholder="Network"
            />
          );
        }
        return <span className="text-sm text-semantic-text-secondary">{row.network || "-"}</span>;
      },
    },
    {
      header: "Gateway",
      accessor: "gateway",
      renderCell: (row) => {
        if (editingRowId === row.id) {
          return (
            <GenericInput
              label=""
              value={editingRowData?.gateway || ""}
              onChange={(value) => handleEditingRowChange("gateway", value)}
              placeholder="Gateway"
            />
          );
        }
        return <span className="text-sm text-semantic-text-secondary">{row.gateway || "-"}</span>;
      },
    },
    {
      header: "Peer IP",
      accessor: "peerIp",
      renderCell: (row) => {
        if (editingRowId === row.id) {
          return (
            <GenericInput
              label=""
              value={editingRowData?.peerIp || ""}
              onChange={(value) => handleEditingRowChange("peerIp", value)}
              placeholder="Peer IP"
            />
          );
        }
        return <span className="text-sm text-semantic-text-secondary">{row.peerIp || "-"}</span>;
      },
    },
    {
      header: "Mask",
      accessor: "mask",
      renderCell: (row) => {
        if (editingRowId === row.id) {
          return (
            <GenericInput
              label=""
              value={editingRowData?.mask ? String(editingRowData.mask) : ""}
              onChange={(value) => handleEditingRowChange("mask", value)}
              placeholder="Mask"
            />
          );
        }
        return <span className="text-sm text-semantic-text-secondary">{row.mask ? String(row.mask) : "-"}</span>;
      },
    },
    {
      header: "Actions",
      renderCell: (row) => {
        if (editingRowId === row.id) {
          return (
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleSaveP2PRow(row.id)}
                className="flex items-center justify-center w-8 h-8 border border-semantic-border-secondary rounded text-green-600 hover:bg-green-50"
                title="Save"
              >
                <IconCheck className="w-5 h-5" />
              </button>
              <button
                onClick={handleCancelP2PRow}
                className="flex items-center justify-center w-8 h-8 border border-semantic-border-secondary rounded text-red-600 hover:bg-red-50"
                title="Cancel"
              >
                <IconCancel className="w-5 h-5" />
              </button>
            </div>
          );
        }
        return (
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleEditP2PRow(row.id)}
              className="
              flex items-center justify-center
              border border-semantic-border-secondary
              rounded
              text-semantic-text-secondary
              hover:bg-semantic-state-active
              w-10 h-10
            "
              title="Edit"
            >
              <IconEdit className="w-10 h-10" />
            </button>
            <button
              onClick={() => handleDeleteP2PRow(row.id)}
              className="
              flex items-center justify-center
              p-2
              border border-semantic-border-secondary
              rounded
              bg-red-50
              text-red-600
              hover:bg-red-100
              w-10 h-10"
              title="Delete"
            >
              <IconDelete className="w-5 h-5" />
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <GenericCard isCollapsible={false} className="p-5">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-md font-semibold text-semantic-text-primary">
          P2P Addressing
        </h3>
        <GenericButton
            label={
            <div className="flex items-center gap-2">
                <IconPlus className="w-5 h-5" />
                <span>Add</span>
            </div>
            }
          variant="secondary"
          size="sm"
          onClick={handleAddP2PRow}
        />
      </div>
      
      {p2pRows.length === 0 ? (
        <div className="text-center py-8 text-semantic-text-secondary">
          No P2P addressing entries. Click &quot;Add more&quot; to add one.
        </div>
      ) : (
        <GenericTable
          columns={p2pTableColumns}
          data={p2pRows}
          rowKey={(row) => row.id}
          tableClassName="bg-semantic-bg-primary"
          width="100%"
          isFooterVisible={false}
          initialRowsPerPage={10}
          rowsPerPageOptions={[5, 10, 20]}
        />
      )}
    </GenericCard>
  );
};

